import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { throwError, combineLatest, Observable } from 'rxjs';
import { map, catchError, take, filter, switchMap, shareReplay } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { AppState, ConfigService } from '@fcom/core';
import { LoginActions } from '@fcom/core/actions';
import { SentryLogger } from '@fcom/core/services/sentry/sentry.logger';
import { loginToken, profile } from '@fcom/core/selectors';
import {
  Profile,
  LoginGender,
  CasLoginResponse,
  LoginResponse,
  ProfileRequestType,
  ProfileRequestCache,
  ParsedPhone,
} from '@fcom/core-api/login';
import { parseProfile } from '@fcom/common/login/services';
import { getCountryOptions } from '@fcom/service-forms/services/utils';
import { SIEBEL_SUPPORTED_COUNTRY_CODES } from '@fcom/loyalty-core/interfaces';
import { isPresent } from '@fcom/core/utils';
import { SelectOption } from '@fcom/common';

import {
  UpdateProfileResponseData,
  UpdateProfileSubmissionData,
  MailingAddressSubmissionData,
  MarketingLanguageSubmissionData,
  PersonalInformationSubmissionData,
  PassportInformationSubmissionData,
  GuardianInformationSubmissionData,
  PaymentCards,
  PaymentCardsResponse,
  PaymentCardData,
  TravelProfileSubmissionData,
} from '../interfaces';

const isSuccessResponse = (o: any): o is LoginResponse => !!o && o.profile && o.profile.partStatus;

@Injectable()
export class ProfileService {
  supportedLanguages = ['CH', 'EN', 'FI', 'FR', 'DE', 'IT', 'JA', 'KO', 'RU', 'ES', 'SV'];
  private accessToken$: Observable<string>;
  private memberNumber$: Observable<string>;
  private profile$: Observable<Profile>;
  private fullProfile$: Observable<Profile>;

  constructor(
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private store$: Store<AppState>,
    private configService: ConfigService,
    private languageService: LanguageService
  ) {
    this.profile$ = this.store$.pipe(profile(), take(1));
    this.memberNumber$ = this.profile$.pipe(map((memberProfile: Profile) => memberProfile.memberNumber));
    this.accessToken$ = this.store$.pipe(loginToken(), filter(Boolean), take(1)) as Observable<string>;
    // eslint-disable-next-line rxjs/no-sharereplay
    this.fullProfile$ = this.fetchProfile().pipe(shareReplay(1));
  }

  updatePersonalInformation(
    formValues: PersonalInformationSubmissionData,
    parsedMobilePhone: ParsedPhone
  ): Observable<UpdateProfileResponseData> {
    return combineLatest([this.profile$, this.memberServiceRequest(formValues)]).pipe(
      map(([memberProfile, response]) => {
        const newProfile = {
          ...memberProfile,
          dateOfBirth: formValues.dateOfBirth,
          email: formValues.email,
          mobilePhone: formValues.phoneNumber,
          gender:
            formValues.gender === LoginGender.MALE
              ? LoginGender.MALE
              : formValues.gender === LoginGender.FEMALE
                ? LoginGender.FEMALE
                : undefined,
        };

        if (isPresent(parsedMobilePhone)) {
          newProfile.parsedMobilePhone = parsedMobilePhone;
        }

        this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));
        return response;
      })
    );
  }

  updateMailingAddress(formValues: MailingAddressSubmissionData): Observable<UpdateProfileResponseData> {
    return combineLatest([this.profile$, this.memberServiceRequest(formValues)]).pipe(
      map(([memberProfile, response]) => {
        const newProfile = {
          ...memberProfile,
          addresses: [formValues.addresses[0]],
        };
        this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));
        return response;
      })
    );
  }

  updateMarketingLanguage(formValues: MarketingLanguageSubmissionData): Observable<UpdateProfileResponseData> {
    return this.memberServiceRequest(formValues);
  }

  updatePassportInformation(formValues: PassportInformationSubmissionData): Observable<UpdateProfileResponseData> {
    return combineLatest([this.profile$, this.memberServiceRequest(formValues)]).pipe(
      map(([memberProfile, response]) => {
        const newProfile = {
          ...memberProfile,
          passport: formValues.travelProfile.passportInformation,
        };
        this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));
        return response;
      })
    );
  }

  updateGuardianInformation(formValues: GuardianInformationSubmissionData): Observable<UpdateProfileResponseData> {
    return this.profile$.pipe(
      map((memberProfile) => {
        if (!memberProfile.isJuniorMember) {
          throw new Error('Tried to update guardian information on non-Junior profile');
        }
        return memberProfile;
      }),
      switchMap((memberProfile: Profile) =>
        this.memberServiceRequest(formValues).pipe(
          map((response) => {
            const newProfile = {
              ...memberProfile,
              guardianName: formValues.guardianName,
              guardianPhoneNumber: formValues.guardianPhoneNumber,
              guardianEmail: formValues.guardianEmail,
              guardianMemberNumber: formValues.guardianMemberNumber,
            };
            this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));
            return response;
          })
        )
      )
    );
  }

  updateTravelProfile(formValues: TravelProfileSubmissionData): Observable<UpdateProfileResponseData> {
    return combineLatest([this.profile$, this.memberServiceRequest(formValues)]).pipe(
      map(([memberProfile, response]) => {
        const newProfile = {
          ...memberProfile,
          householdSize: formValues.householdSize,
          childrenBirthYears: formValues.childrenBirthYears,
          topicsOfInterest: formValues.topicsOfInterest,
          preferredSeatType: formValues.travelProfile.seatPreference,
          preferredMealType: formValues.travelProfile.mealPreference,
        };
        this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));
        return response;
      })
    );
  }

  getProfile(): Observable<Profile> {
    return this.store$.pipe(profile(), filter(Boolean), take(1)) as Observable<Profile>;
  }

  getFullProfile(cache = true): Observable<Profile> {
    if (!cache) {
      // eslint-disable-next-line rxjs/no-sharereplay
      this.fullProfile$ = this.fetchProfile().pipe(shareReplay(1));
    }
    return this.fullProfile$;
  }

  getCountryOptions(defaultOption?: SelectOption): Observable<SelectOption[]> {
    return getCountryOptions(this.languageService, defaultOption).pipe(
      map((options) => options.filter((option) => SIEBEL_SUPPORTED_COUNTRY_CODES.includes(option.value.toLowerCase())))
    );
  }

  getCountryRegionOptions(countryCode: string): Observable<SelectOption[]> {
    return this.languageService.translate(`COUNTRY_REGIONS.${countryCode}`).pipe(
      map((regions) =>
        regions
          ? Object.keys(regions).map(
              (region) =>
                ({
                  value: region.toUpperCase(),
                  name: regions[region],
                }) as SelectOption
            )
          : []
      )
    );
  }

  getLanguageOptions(): Observable<SelectOption[]> {
    return this.languageService.translate('SERVICE_FORMS.serviceForms.fields.language').pipe(
      map((languages) =>
        this.supportedLanguages.map(
          (lang) =>
            ({
              value: lang.toUpperCase(),
              name: languages[lang],
            }) as SelectOption
        )
      )
    );
  }

  getParsedPaymentCards(): Observable<PaymentCardData[]> {
    return this.getPaymentCards().pipe(
      map((result: PaymentCardsResponse) =>
        result.tokens.map((card: PaymentCardData) => {
          return {
            ...card,
            svgName: PaymentCards[card.vendor],
            lastDigits: card.token.slice(-4),
            expiryDate: `${card.expiry.substr(0, 2)}/${card.expiry.substr(2)}`,
          };
        })
      )
    );
  }

  getPaymentCards(): Observable<PaymentCardsResponse> {
    const apiUrl = `${this.configService.cfg.oneClickPaymentUrl}/tokens`;
    const keyId = this.configService.cfg.oneClickPaymentApiKey;
    const accessToken$ = this.accessToken$;
    const memberNumber$ = this.memberNumber$;

    return combineLatest([accessToken$, memberNumber$]).pipe(
      switchMap(([token, memberNumber]) => {
        /* eslint-disable camelcase */
        const httpOptions = {
          headers: new HttpHeaders({
            KeyId: keyId,
            oauth_token: token,
            AYUID: memberNumber,
          }),
        };
        /* eslint-enable camelcase */

        return this.http.get<PaymentCardsResponse>(apiUrl, httpOptions).pipe(
          map((response) => {
            if (response.tokens) {
              return response;
            }
            throw new Error('Error when requesting payment cards');
          }),
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  deletePaymentCard(cardToken: string): Observable<HttpResponse<Response>> {
    const apiUrl = `${this.configService.cfg.oneClickPaymentUrl}/delete`;
    const keyId = this.configService.cfg.oneClickPaymentApiKey;
    const accessToken$ = this.accessToken$;
    const memberNumber$ = this.memberNumber$;

    return combineLatest([accessToken$, memberNumber$]).pipe(
      switchMap(([accessToken, memberNumber]) => {
        /* eslint-disable camelcase */
        const headers = new HttpHeaders({
          KeyId: keyId,
          oauth_token: accessToken,
          AYUID: memberNumber,
        });
        /* eslint-enable camelcase */

        const data = {
          token: cardToken,
        };

        return this.http.post<Response>(apiUrl, data, { headers, observe: 'response' }).pipe(
          map((response) => {
            if (response.ok) {
              return response;
            }
            throw new Error('Error when deleting payment card');
          }),
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  fetchProfile(): Observable<Profile> {
    return this.http
      .post<CasLoginResponse>(
        this.configService.cfg.loyaltyServices.legacy.apiProfileUrl,
        {
          profileRequest: {
            type: ProfileRequestType.FULL,
            cache: ProfileRequestCache.REFRESH,
          },
        },
        {
          headers: new HttpHeaders({
            Accept: 'application/json',
          }),
        }
      )
      .pipe(
        map((response: CasLoginResponse) => {
          if (isSuccessResponse(response)) {
            const parsedProfile = parseProfile(response.profile);
            this.store$.dispatch(LoginActions.setProfile({ profile: parsedProfile }));
            return parsedProfile;
          } else {
            throw new Error('Fetching profile failed');
          }
        }),
        // eslint-disable-next-line rxjs/no-sharereplay
        shareReplay(1),
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        catchError((err) => {
          this.sentryLogger.warn(err.message, { error: err });
          return throwError(() => err);
        })
      );
  }

  private memberServiceRequest(data: UpdateProfileSubmissionData): Observable<UpdateProfileResponseData> {
    const apiUrl = this.configService.cfg.loyaltyServices.legacy.loyaltyMemberUrl;
    const accessToken$ = this.accessToken$;

    return accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
          }),
        };

        return this.http.put<UpdateProfileResponseData>(apiUrl, data, httpOptions).pipe(
          map((response) => {
            if (response.status.message === 'OK') {
              return response;
            }
            throw new Error('Error submitting update profile request');
          }),
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }
}
